import type { JudgeConfig } from '../../utils/judges';
import { requestScore, type ScoreResponse } from './validate-api';
import {
  scorableTurnIndices,
  type RunScore,
  type ValidateTurn,
} from './validate-runs';

/** A scorer verdict as the run keeps it. */
export const toRunScore = (verdict: ScoreResponse): RunScore => ({
  status: 'scored',
  verdict: {
    scores: verdict.scores,
    autoFail: verdict.autoFail,
    ...(verdict.criteria?.length ? { criteria: verdict.criteria } : {}),
  },
  summary: verdict.summary,
  reasons: verdict.reasons ?? {},
  whyFailed: verdict.whyFailed ?? null,
  toneNote: verdict.toneNote ?? null,
});

/**
 * How a caught error is worded where it is kept or shown. The default is the
 * browser's: the routes already answered with a message that is safe to show,
 * so an `Error`'s own text is used. The scheduler runs this same code on the
 * server and stores what comes back for the browser to read later, so it
 * passes its own (`run-task.ts`) that never lets provider text through.
 */
export type DescribeError = (err: unknown, fallback: string) => string;

export const describeErrorMessage: DescribeError = (err, fallback) =>
  err instanceof Error ? err.message : fallback;

const errorScore = (err: unknown, describeError: DescribeError): RunScore => ({
  status: 'error',
  message: describeError(err, 'Scoring failed.'),
});

/** Score one turn — or the whole conversation, with `focusIndex: null`. */
export const scoreFocus = async (
  args: {
    transcript: ValidateTurn[];
    focusIndex: number | null;
    scenario?: string;
    expectation?: string;
    /** Passed on to `score` untouched; see `requestScore`. */
    judge?: JudgeConfig | null;
  },
  {
    score = requestScore,
    signal,
    describeError = describeErrorMessage,
  }: {
    score?: typeof requestScore;
    signal?: AbortSignal;
    describeError?: DescribeError;
  } = {}
): Promise<RunScore> => {
  try {
    return toRunScore(await score(args, signal));
  } catch (err) {
    return errorScore(err, describeError);
  }
};

/**
 * How many turns are scored at once. Each is its own model call; a six-turn
 * scenario scored one at a time would add most of a minute, and scoring all
 * at once would stack every suite case's calls on top of each other.
 */
export const TURN_SCORE_CONCURRENCY = 3;

/**
 * Score every scorable bot reply in a conversation (`scorableTurnIndices`),
 * each in the context of the whole transcript. Resolves with a score per
 * turn — an error score for a turn whose call failed, never a rejection, so
 * one bad call does not lose the rest.
 */
export const scoreTurns = async (
  args: {
    transcript: ValidateTurn[];
    scenario?: string;
    expectation?: string;
    judge?: JudgeConfig | null;
  },
  deps: {
    score?: typeof requestScore;
    signal?: AbortSignal;
    describeError?: DescribeError;
    onTurn?: (index: number, score: RunScore) => void;
  } = {}
): Promise<Record<string, RunScore>> => {
  const queue = scorableTurnIndices(args.transcript);
  const out: Record<string, RunScore> = {};
  const worker = async () => {
    for (;;) {
      const index = queue.shift();
      if (index === undefined || deps.signal?.aborted) return;
      // eslint-disable-next-line no-await-in-loop
      const result = await scoreFocus({ ...args, focusIndex: index }, deps);
      out[String(index)] = result;
      deps.onTurn?.(index, result);
    }
  };
  await Promise.all(
    Array.from({ length: TURN_SCORE_CONCURRENCY }, () => worker())
  );
  return out;
};
